import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Download,
  ExternalLink,
  Plus,
  Search,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { SupportTicketComposerModal, SupportTicketListItem } from '@/components/support/SupportTicketShared';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FilterSelect } from '@/components/ui/FilterSelect';
import { Input } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { getHelpNavItems, type ModuleAccessMap } from '@/components/layout/navigation.config';
import { useModuleAccessMap } from '@/hooks/useNavAccess';
import { useAuth } from '@/hooks/useAuth';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { listSupportTickets } from '@/services/supportTickets.service';
import { getLegacyOrigin } from '@/lib/appStage';
import {
  DEFAULT_HELP_TICKET_LIST_STATE,
  HELP_TICKET_OPENED_OPTIONS,
  HELP_TICKET_PRIORITY_OPTIONS,
  HELP_TICKET_SORT_OPTIONS,
  HELP_TICKET_STATUS_OPTIONS,
  HELP_TICKET_TYPE_OPTIONS,
  hasActiveHelpTicketFilters,
  helpTicketListParams,
  helpTicketListStorageKey,
  sanitizeHelpTicketListState,
  type HelpTicketListState,
} from '@/pages/help/helpTicketListState';

const TICKETS_PER_PAGE = 15;

interface HelpLink {
  label: string;
  href: string;
  external?: boolean;
}

interface HelpTopic {
  id: string;
  title: string;
  summary: string;
  /** Owning per-account module — the whole section hides when it's disabled. */
  moduleKey?: string;
  startHere?: HelpLink;
  tasks: string[];
  links?: HelpLink[];
  note?: string;
}

interface HelpDownload {
  id: string;
  title: string;
  description: string;
  href: string;
  platform?: string;
}

const DOWNLOADS: HelpDownload[] = [
  {
    id: 'scanner-bridge-windows',
    title: 'Prospera Scanner Bridge',
    description:
      'Desktop helper that connects your ID scanner to Prospera so you can scan licenses directly into new leads, and can present the tap-to-check-in link on a front desk reader pad.',
    href: `${getLegacyOrigin()}/downloads/ProsperaScanner-Setup.exe`,
    platform: 'Windows',
  },
  {
    id: 'scanner-bridge-macos',
    title: 'Prospera Scanner Bridge',
    description:
      'Desktop helper that connects your ID scanner to Prospera so you can scan licenses directly into new leads.',
    href: `${getLegacyOrigin()}/downloads/ProsperaScanner-Setup.pkg`,
    platform: 'macOS',
  },
];

/** First href segment → owning module key (mirrors the sidebar's nav tags) so
 *  Help only surfaces guides/links for areas the account actually has. */
const HELP_HREF_MODULES: Record<string, string> = {
  inbox: 'dashboard', payments: 'payments', 'gift-cards': 'payments', loans: 'payments',
  memberships: 'memberships', packages: 'packages', inventory: 'inventory',
  messages: 'messaging', marketing: 'marketing',
  leads: 'crm', plans: 'crm', crm: 'crm', quotes: 'crm',
  'lab-orders': 'labs', qa: 'qa', documents: 'forms', surveys: 'forms',
  schedule: 'scheduling', appointments: 'scheduling', tasks: 'tasks',
  alerts: 'alerts', 'video-calls': 'video', peptides: 'axiom',
  'ai-assistant': 'ai assistant',
};

function helpHrefEnabled(href: string, access: ModuleAccessMap): boolean {
  const key = href === '/' ? 'dashboard' : HELP_HREF_MODULES[href.replace(/^\//, '').split(/[/?#]/)[0]];
  if (!key) return true; // core pages (patients, settings, help…) always show
  const entry = access[`/${key}`];
  return !entry || entry.enabled;
}

const GUIDE_SECTIONS: HelpTopic[] = [
  {
    id: 'getting-started',
    title: 'Getting Started and Access',
    summary: 'Start here for sign-in, the dashboard, and the fastest ways to move around Prospera during daily work.',
    startHere: { label: 'Open the Dashboard', href: '/' },
    tasks: [
      'Sign in from the staff login page with your username and password, or use the corporate login option when your account is configured for single sign-on.',
      'Use the dashboard as your launch point for pending messages, open tasks, active leads, and plan activity. The dashboard cards jump directly into the related work queues.',
      'Use the main sidebar to move between modules, and use the patient search field in the sidebar when you need to jump straight into a patient record.',
      'Use the patient portal login, public check-in, and video join pages only for those specific access flows.',
    ],
    links: [
      { label: 'Staff Login', href: '/login' },
      { label: 'Patient Portal Login', href: '/portal/login' },
      { label: 'Check-In', href: '/checkin' },
      { label: 'Video Join', href: '/video-join' },
    ],
  },
  {
    id: 'front-desk-tap',
    title: 'Front Desk Tap Check-In',
    summary: 'Let patients check themselves in by holding their phone to a pad at the front desk. Their chart opens on the front desk computer.',
    startHere: { label: 'Open Front Desk Reader settings', href: '/settings?tab=frontdesk' },
    tasks: [
      'On the front desk computer, open Settings, choose Front Desk Reader, and turn on "Use this computer as a front desk reader". Pick the desk for this location, or ask an administrator to add one.',
      'Copy the desk’s check-in link and write it to an NFC sticker with a free phone app such as NFC Tools, then place the sticker under a "Tap to check in" sign. If the Prospera Scanner Bridge is running with a reader pad attached, you can send the link to the pad instead.',
      'Patients wake their phone, hold it to the pad, and tap the banner that appears. The Next Health app opens and lets the front desk know they have arrived; if they have a visit booked today it is checked in.',
      'Within a few seconds this computer opens the patient’s chart on the Appointments tab. Only one screen opens it, even when several tabs are open.',
      'If a sticker is lost or copied, an administrator can get a new link for the desk from the same settings page. Old stickers stop working, so rewrite them.',
    ],
    links: [
      { label: 'Settings', href: '/settings?tab=frontdesk' },
      { label: 'Schedule', href: '/schedule' },
    ],
  },
  {
    id: 'patients-records',
    title: 'Patients and Records',
    summary: 'Use the patient list and patient detail record to manage the full timeline of a patient relationship.',
    startHere: { label: 'Open Patients', href: '/patients' },
    tasks: [
      'Search for a patient from the Patients page or the sidebar search box, then open the patient record to review the full chart.',
      'Use the patient sub-navigation to move through demographics, health information, financing, plans, tasks, appointments, payments, memberships, packages, documents, forms, quality assurance, and notes.',
      'Use the communication log inside the patient record when you need one place to review conversations, activity, and follow-up context tied to that specific patient.',
      'Use the patient detail shell when you need patient history, right-side tools, or AI context while working through the record.',
    ],
    links: [
      { label: 'Patients', href: '/patients' },
      { label: 'Documents', href: '/documents' },
      { label: 'Forms', href: '/surveys' },
    ],
  },
  {
    id: 'leads-crm-quotes',
    title: 'Leads, CRM, and Quotes',
    summary: 'Track incoming demand, organize follow-up, and move prospects toward consults, plans, and quotes.',
    startHere: { label: 'Open CRM Dashboard', href: '/crm' },
    tasks: [
      'Use the CRM dashboard as the section landing page, then open the Pipeline workspace when you need the kanban view for lead activity and follow-up.',
      'Use the Leads page when you want filters, tabs, and table tools for new, done, bad, SMS, or patient form leads.',
      'Create a new lead from the dedicated New Lead page when staff are entering a prospect manually.',
      'Open a quote when you need to build or review pricing details tied to a lead or treatment path.',
    ],
    links: [
      { label: 'CRM Dashboard', href: '/crm' },
      { label: 'CRM Pipeline', href: '/crm/pipeline' },
      { label: 'Leads', href: '/leads' },
      { label: 'New Lead', href: '/leads/new' },
    ],
  },
  {
    id: 'appointments-schedule-tasks',
    title: 'Appointments, Schedule, and Tasks',
    summary: 'Coordinate calendars, schedule new visits, and keep follow-up work moving.',
    startHere: { label: 'Open Schedule', href: '/schedule' },
    tasks: [
      'Use Schedule for calendar-based work such as reviewing weekly, daily, monthly, or overview views, filtering resources, and moving around the calendar.',
      'Click an empty slot in Schedule when you want to create a new appointment with a pre-filled time, and click an appointment card when you need to review its details.',
      'Use the Appointments page when you need appointment management outside the calendar view.',
      'Use Tasks to work through past-due, due-today, upcoming, complete, or inactive tasks, and rely on filters when you need to narrow work by facility, assignment, stage, priority, or task group.',
    ],
    links: [
      { label: 'Schedule', href: '/schedule' },
      { label: 'Appointments', href: '/appointments' },
      { label: 'Tasks', href: '/tasks' },
    ],
  },
  {
    id: 'plans-payments-memberships',
    title: 'Plans, Payments, Memberships, and Inventory',
    summary: 'Use these areas when work moves from interest and consultation into pricing, financing, membership, package, and product operations.',
    startHere: { label: 'Open Plans', href: '/plans' },
    tasks: [
      'Use Plans to review inquiry and consult views, open plan detail, and track work after leads move deeper into the care or sales process.',
      'Use Payments for transaction activity, Loans for financing workflows, and Gift Cards when handling stored-value sales or redemption.',
      'Use Memberships and Packages for ongoing programs and bundled offerings that staff manage from the main product.',
      'Use Inventory to manage products, vendors, purchase orders, and inventory reports whenever physical stock or supply workflows are involved.',
    ],
    links: [
      { label: 'Plans', href: '/plans' },
      { label: 'Payments', href: '/payments' },
      { label: 'Loans', href: '/loans' },
      { label: 'Gift Cards', href: '/gift-cards' },
      { label: 'Memberships', href: '/memberships' },
      { label: 'Packages', href: '/packages' },
      { label: 'Inventory', href: '/inventory' },
    ],
  },
  {
    id: 'messages-communication',
    title: 'Messages and Communication',
    summary: 'Work your inbox, review patient communication history, and move quickly from unread activity into the correct patient context.',
    startHere: { label: 'Open Communication Center', href: '/messages' },
    tasks: [
      'Use the Communication Center to review inbound and outbound communication, including email, SMS, calls, and related activity types.',
      'Open a conversation from the inbox when you need to respond or review message history tied to a patient.',
      'Use dashboard message cards for quick triage when you want to jump directly from unread activity into the inbox.',
      'Use patient-specific communication views when you need to keep outreach anchored to a single patient record instead of a global queue.',
    ],
    links: [
      { label: 'Communication Center', href: '/messages' },
      { label: 'Chat View', href: '/messages/chat' },
    ],
  },
  {
    id: 'marketing-campaigns',
    title: 'Marketing, Campaigns, and Templates',
    summary: 'Use Marketing for outbound campaigns, reusable content, audience management, and media assets.',
    startHere: { label: 'Open Marketing', href: '/marketing' },
    tasks: [
      'Use the Marketing dashboard as the hub for campaign and content work across email, SMS, push, audiences, reports, and settings.',
      'Use campaign pages when you are creating or managing outbound email, SMS, or push communication.',
      'Use Templates when you need reusable content, and open the email or SMS builders when you are editing channel-specific template content.',
      'Use Audiences and Photo Library to manage who receives campaigns and what creative assets are available to marketing staff.',
    ],
    links: [
      { label: 'Marketing Dashboard', href: '/marketing' },
      { label: 'Email Campaigns', href: '/marketing/email-campaigns' },
      { label: 'SMS Campaigns', href: '/marketing/sms-campaigns' },
      { label: 'Push Campaigns', href: '/marketing/push-campaigns' },
      { label: 'Automations', href: '/marketing/automations' },
      { label: 'Templates', href: '/marketing/templates' },
      { label: 'Contact Lists', href: '/marketing/contact-lists' },
      { label: 'Photo Library', href: '/marketing/photos' },
    ],
  },
  {
    id: 'ai-features',
    moduleKey: 'ai assistant',
    title: 'AI Features in Daily Use',
    summary: 'Prospera includes real AI tools inside the product, but they are specific surfaces rather than a separate AI-only application.',
    startHere: { label: 'Return to the Dashboard', href: '/' },
    tasks: [
      'Use the AI Assistant sidebar on the right side of the app when you want help that is aware of the page you are currently viewing.',
      'Ask the assistant page-specific questions after navigating to a real working page such as Dashboard, Patients, Tasks, or Marketing so the assistant can use the active page context.',
      'Use patient, lead, or plan context when those records are open, because the AI sidebar can ground its responses in the record you are actively viewing.',
      'Use the AI email tools inside the email editor when you want to generate a full email, edit a selected block, suggest alternate text, or optimize email HTML for client compatibility.',
    ],
    links: [
      { label: 'Dashboard', href: '/' },
      { label: 'Patients', href: '/patients' },
      { label: 'Leads', href: '/leads' },
      { label: 'Plans', href: '/plans' },
      { label: 'Marketing Templates', href: '/marketing/templates' },
    ],
  },
  {
    id: 'labs-qa-documents-admin',
    title: 'Labs, QA, Documents, Reports, Settings, and Admin',
    summary: 'Use these sections for supporting operations, account preferences, and system configuration.',
    startHere: { label: 'Open Settings', href: '/settings' },
    tasks: [
      'Use Labs when working lab-order workflows, and use Quality Assurance when you need to review or manage QA-related operational work.',
      'Use Documents and Reports for shared content and reporting views that sit outside the patient detail shell.',
      'Use Settings for your own profile, password, and notification preferences.',
      'Use Admin only when your role has access and you need to manage account, practice, plans, appointments, payments, templates, users, reports, or AI prompt configuration.',
    ],
    links: [
      { label: 'Labs', href: '/lab-orders' },
      { label: 'Quality Assurance', href: '/qa' },
      { label: 'Documents', href: '/documents' },
      { label: 'Reports', href: '/reports' },
      { label: 'Settings', href: '/settings' },
      { label: 'Admin', href: '/admin/account' },
    ],
  },
];

export function HelpPage() {
  const { user, isSuperAdmin, isMasterAdmin } = useAuth();
  const [composerOpen, setComposerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to page 1 whenever a new search lands so we never show an empty
  // page that would have been valid for the previous result set.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Force the account scope on the Help surface even for super admins so
  // /help is always the practice-facing view of the user's current account.
  // The admin queue at /admin/support-tickets remains the cross-account
  // surface. The server still applies the userLevel < 3 self-only rule on
  // top of this, so passing accountID does not widen any user's visibility.
  const helpAccountId = user?.accountId;

  const [storedListState, setStoredListState] = useLocalStorage<unknown>(
    helpTicketListStorageKey(user?.id, user?.accountId),
    DEFAULT_HELP_TICKET_LIST_STATE,
  );
  const listState = useMemo(() => sanitizeHelpTicketListState(storedListState), [storedListState]);
  const listParams = helpTicketListParams(listState);
  const filtersActive = hasActiveHelpTicketFilters(listState);

  // Page 1 is set in the same update as the new choice, so the first request
  // after a change already asks for page 1.
  const updateListState = (patch: Partial<HelpTicketListState>) => {
    setStoredListState({ ...listState, ...patch });
    setPage(1);
  };
  const clearFilters = () => updateListState({ status: '', type: '', priority: '', opened: '' });

  const supportTicketsQuery = useQuery({
    queryKey: ['support-ticket-list', 'help', helpAccountId, debouncedSearch, page, listParams],
    queryFn: () => listSupportTickets({
      search: debouncedSearch || undefined,
      accountID: helpAccountId,
      page,
      perPage: TICKETS_PER_PAGE,
      ...listParams,
    }),
    // Keep the current rows on screen while a filter, sort or page change loads.
    placeholderData: keepPreviousData,
  });

  let emptyMessage = 'No support tickets have been submitted for this account yet.';
  if (filtersActive && debouncedSearch) {
    emptyMessage = 'No tickets match your search and filters.';
  } else if (filtersActive) {
    emptyMessage = 'No tickets match these filters.';
  } else if (debouncedSearch) {
    emptyMessage = 'No tickets match your search.';
  }

  const ticketMeta = supportTicketsQuery.data?.meta ?? {};
  const totalTickets = ticketMeta.total ?? 0;
  const currentPage = ticketMeta.page ?? page;
  const currentPerPage = ticketMeta.perPage ?? TICKETS_PER_PAGE;

  const externalTools = useMemo(
    () =>
      getHelpNavItems(user?.userLevel ?? 0, isSuperAdmin, isMasterAdmin)
        .filter((item) => item.external && item.href)
        .map((item) => ({ label: item.label, href: item.href! })),
    [user?.userLevel, isSuperAdmin, isMasterAdmin],
  );

  // Module-filtered guides: drop links owned by disabled modules, and whole
  // sections when their owning module is off or no links survive.
  const moduleAccess = useModuleAccessMap();
  const guideSections = useMemo(
    () =>
      GUIDE_SECTIONS
        .filter((sec) => {
          if (!sec.moduleKey) return true;
          const entry = moduleAccess[`/${sec.moduleKey}`];
          return !entry || entry.enabled;
        })
        .map((sec) => ({
          ...sec,
          links: (sec.links ?? []).filter((l) => l.external || helpHrefEnabled(l.href, moduleAccess)),
          startHere: sec.startHere && helpHrefEnabled(sec.startHere.href, moduleAccess) ? sec.startHere : undefined,
        }))
        .filter((sec) => (sec.links.length > 0 || !!sec.startHere)),
    [moduleAccess],
  );

  const hasResults = guideSections.length > 0 || externalTools.length > 0;

  return (
    <div className="mx-auto max-w-6xl space-y-8 px-6 py-6 md:px-8 lg:px-10">
      <PageHeader
        title="Help Center"
        actions={(
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setComposerOpen(true)}>
            Submit Ticket
          </Button>
        )}
      />

      <section className="space-y-4">
        <Card className="border-slate-200 bg-white">
          <CardHeader>
            <CardTitle className="text-base">Tickets</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search tickets, subject, requester…"
              leftAddon={<Search className="h-4 w-4 text-slate-400" />}
              aria-label="Search tickets"
            />

            {/* Opened and Sort carry a prefix in their option text, so they get the wider columns. */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(0,1fr))_minmax(0,1.25fr)_minmax(0,1.35fr)]">
              <FilterSelect
                label="Filter by status"
                options={HELP_TICKET_STATUS_OPTIONS}
                value={listState.status}
                onChange={(status) => updateListState({ status })}
              />
              <FilterSelect
                label="Filter by type"
                options={HELP_TICKET_TYPE_OPTIONS}
                value={listState.type}
                onChange={(type) => updateListState({ type })}
              />
              <FilterSelect
                label="Filter by priority"
                options={HELP_TICKET_PRIORITY_OPTIONS}
                value={listState.priority}
                onChange={(priority) => updateListState({ priority })}
              />
              <FilterSelect
                label="Filter by opened date"
                options={HELP_TICKET_OPENED_OPTIONS}
                value={listState.opened}
                onChange={(opened) => updateListState({ opened })}
              />
              <FilterSelect
                label="Sort tickets"
                options={HELP_TICKET_SORT_OPTIONS}
                value={listState.sort}
                onChange={(sort) => updateListState({ sort })}
                className="sm:col-span-2 lg:col-span-1"
              />
            </div>

            {filtersActive && (
              <div className="flex justify-end">
                <Button variant="link" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              </div>
            )}

            <div className="space-y-3">
              {supportTicketsQuery.isLoading && (
                <div className="py-8 text-center text-sm text-slate-500">Loading support tickets…</div>
              )}

              {!supportTicketsQuery.isLoading && (supportTicketsQuery.data?.data.length ?? 0) === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                  {emptyMessage}
                </div>
              )}

              {(supportTicketsQuery.data?.data ?? []).map((ticket) => (
                <SupportTicketListItem
                  key={ticket.supportTicketId}
                  ticket={ticket}
                  href={`/help/tickets/${ticket.supportTicketId}`}
                  managerView={false}
                />
              ))}
            </div>

            {totalTickets > currentPerPage && (
              <Pagination
                total={totalTickets}
                page={currentPage}
                pageSize={currentPerPage}
                onPageChange={setPage}
              />
            )}
          </CardBody>
        </Card>
      </section>

      {DOWNLOADS.length > 0 && (
        <section className="space-y-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle className="text-base">Downloads</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              {DOWNLOADS.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 rounded-lg border border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                      {item.platform && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          {item.platform}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{item.description}</p>
                  </div>
                  <Button
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={() => window.location.assign(item.href)}
                  >
                    Download
                  </Button>
                </div>
              ))}
            </CardBody>
          </Card>
        </section>
      )}

      {!hasResults && (
        <Card className="border-slate-200 bg-white">
          <CardBody className="py-10 text-center">
            <p className="text-sm font-medium text-slate-900">No matching help topics found.</p>
            <p className="mt-1 text-sm text-slate-500">Try a broader search such as `patients`, `tasks`, `AI`, or `login`.</p>
          </CardBody>
        </Card>
      )}

      {guideSections.length > 0 && (
        <section className="space-y-5">
          <h2 className="text-lg font-semibold text-slate-900">Guides</h2>

          <div className="space-y-5">
            {guideSections.map((section) => (
              <Card key={section.id} id={section.id} className="scroll-mt-24 border-slate-200 bg-white">
                <CardHeader>
                  <div className="space-y-3">
                    <CardTitle className="text-base">{section.title}</CardTitle>
                    <p className="text-sm text-slate-600">{section.summary}</p>
                    {section.startHere && (
                      <div className="inline-flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        <span className="font-medium text-slate-900">Start here:</span>
                        <Link to={section.startHere.href} className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700">
                          {section.startHere.label}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardBody className="space-y-5">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">How to use it</h3>
                    <ul className="mt-3 space-y-2">
                      {section.tasks.map((task) => (
                        <li key={task} className="flex gap-2 text-sm text-slate-600">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                          <span>{task}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {section.links && section.links.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Related pages</h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {section.links.map((link) => (
                          <Link
                            key={`${section.id}-${link.href}-${link.label}`}
                            to={link.href}
                            className="rounded-full border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {section.note && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      {section.note}
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        </section>
      )}

      {externalTools.length > 0 && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">External tools from the main nav</h2>
            <p className="mt-1 text-sm text-slate-500">These entries are reachable from the sidebar, but they open outside the main Prospera application.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {externalTools.map((tool) => (
              <Card key={tool.href} className="border-slate-200 bg-white">
                <CardBody className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{tool.label}</p>
                    <p className="mt-1 text-xs text-slate-500">{tool.href}</p>
                  </div>
                  <a
                    href={tool.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Open external site
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </CardBody>
              </Card>
            ))}
          </div>
        </section>
      )}

      <SupportTicketComposerModal
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
      />
    </div>
  );
}
